import type { Organization } from '@cmoh/shared'
import { Check, ChevronDown, ExternalLink, Loader2, Search } from 'lucide-react'
import { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/use-translation'
import { openOrganizationInNewTab } from '@/lib/tab-organization'
import { cn } from '@/lib/utils'
import { useOrganizationsForUsers } from '../hooks/use-organizations'
import { useSwitchOrganization } from '../hooks/use-switch-organization'

function parseHostname(url: string): string | null {
  try {
    return new URL(url).hostname
  } catch {
    return null
  }
}

function resolveLogoSrc(logo?: string | null, websiteUrl?: string | null): string | null {
  if (logo) {
    return logo
  }
  const host = websiteUrl ? parseHostname(websiteUrl) : null
  return host ? `https://www.google.com/s2/favicons?domain=${host}&sz=64` : null
}

function OrgLogo({
  name,
  logoSrc,
  variant = 'list',
}: {
  name: string
  logoSrc: string | null
  variant?: 'trigger' | 'list'
}) {
  const [imgError, setImgError] = useState(false)

  return (
    <span
      className={cn(
        'flex h-[22px] w-[22px] shrink-0 items-center justify-center overflow-hidden rounded-md text-[12px] font-bold leading-none text-primary',
        variant === 'trigger' ? 'bg-white/[0.95]' : 'bg-primary-50',
      )}
    >
      {logoSrc && !imgError ? (
        <img
          src={logoSrc}
          alt={name}
          className="h-[18px] w-[18px] object-contain"
          onError={() => setImgError(true)}
        />
      ) : (
        name.charAt(0).toUpperCase()
      )}
    </span>
  )
}

interface SuperAdminOrgSwitcherProps {
  currentOrganization: Organization | null
}

export function SuperAdminOrgSwitcher({ currentOrganization }: SuperAdminOrgSwitcherProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const t = useTranslation()
  const { data } = useOrganizationsForUsers()
  const { switchOrganization, isSwitching } = useSwitchOrganization()

  const organizations = data?.organizations ?? []
  const currentOrgWebsiteUrl = organizations.find((o) => o.id === currentOrganization?.id)
    ?.primaryWebsiteUrl
  const filteredOrgs = organizations.filter((o) =>
    o.name.toLowerCase().includes(query.trim().toLowerCase()),
  )

  const handleSelect = async (orgId: string) => {
    if (orgId === currentOrganization?.id) {
      setOpen(false)
      return
    }
    setOpen(false)
    await switchOrganization(orgId)
  }

  const handleOpenInNewTab = (orgId: string) => {
    setOpen(false)
    openOrganizationInNewTab(orgId)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setQuery('')
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={isSwitching}
          aria-label={t('superAdmin.orgSwitcher.label')}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            'flex w-full items-center gap-2.5 rounded-[10px] bg-white/[0.08] px-2.5 py-2 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]',
            'text-sm font-medium text-white hover:bg-white/[0.14]',
            'transition-all duration-200 ease-in-out cursor-pointer',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {isSwitching ? (
            <Loader2 className="h-[22px] w-[22px] shrink-0 animate-spin p-0.5 text-white" />
          ) : (
            <OrgLogo
              name={currentOrganization?.name ?? '?'}
              logoSrc={resolveLogoSrc(currentOrganization?.logo, currentOrgWebsiteUrl)}
              variant="trigger"
            />
          )}
          <span className="truncate flex-1 text-left text-sm font-medium">
            {currentOrganization?.name ?? t('superAdmin.orgSwitcher.noOrganization')}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 opacity-60 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) overflow-hidden rounded-xl border-0 bg-white p-1.5 shadow-[0_12px_30px_rgba(8,12,40,0.32)]"
        side="bottom"
        align="start"
        sideOffset={6}
      >
        <div className="px-0.5 pb-1.5">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('superAdmin.orgSwitcher.placeholder')}
              aria-label={t('superAdmin.orgSwitcher.placeholder')}
              data-org-search
              className="w-full rounded-lg border bg-neutral-50 py-2 pr-2.5 pl-9 text-[13px] text-neutral-900 outline-none placeholder:text-neutral-400"
            />
          </div>
        </div>
        <div className="flex max-h-[280px] flex-col gap-0.5 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {filteredOrgs.length === 0 ? (
            <div className="py-2.5 text-center text-[13px] text-neutral-400">
              {t('superAdmin.orgSwitcher.noResults')}
            </div>
          ) : (
            filteredOrgs.map((org) => {
              const isActive = currentOrganization?.id === org.id
              return (
                <div
                  key={org.id}
                  className="flex w-full items-center gap-1 rounded-lg transition-colors hover:bg-neutral-100"
                >
                  <button
                    type="button"
                    onClick={() => handleSelect(org.id)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left"
                  >
                    <OrgLogo
                      name={org.name}
                      logoSrc={resolveLogoSrc(undefined, org.primaryWebsiteUrl)}
                      variant="list"
                    />
                    <span className="flex-1 truncate text-sm font-medium text-neutral-900">
                      {org.name}
                    </span>
                    {isActive && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  </button>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t('superAdmin.orgSwitcher.openInNewTab')}
                        onClick={() => handleOpenInNewTab(org.id)}
                        className="mr-1 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-neutral-500 transition-colors duration-200 ease-in-out hover:bg-neutral-200 hover:text-neutral-900"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{t('superAdmin.orgSwitcher.openInNewTab')}</TooltipContent>
                  </Tooltip>
                </div>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
